import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '../widget-api/http-client';
import { fetchPDTransferNotice, type PDTransferNotice } from '../widget-api/pd-transfer';

type PDTransferConfirmOptions = {
  accessToken: string | null | undefined;
  organizationId: number | null;
  /** Слот записи: по нему сервер определяет филиал и список получателей. */
  slotId: number | null | undefined;
  /** Уведомление нужно только на шаге онлайн-оплаты при открытой модалке. */
  enabled: boolean;
};

/**
 * Галочка А.3 на шаге оплаты (Ф8, ТЗ 5.14 п.4): текст уведомления, состояние
 * подтверждения и payload для запроса оплаты. Кнопка «Оплатить» активна только
 * после галочки; без подтверждения сервер вернёт 422 PD_TRANSFER_NOT_CONFIRMED.
 */
export function usePDTransferConfirm({ accessToken, organizationId, slotId, enabled }: PDTransferConfirmOptions) {
  const [notice, setNotice] = useState<PDTransferNotice | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNotice(null);
    setConfirmed(false);
    setError(null);
    if (!enabled || !accessToken || !slotId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchPDTransferNotice(accessToken, organizationId, slotId)
      .then((n) => {
        if (!cancelled) setNotice(n);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Не удалось загрузить уведомление о передаче данных');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, accessToken, organizationId, slotId]);

  /** Применить свежее уведомление из ошибки 422 (список получателей изменился). */
  const applyServerError = useCallback((e: unknown): boolean => {
    if (!(e instanceof ApiError) || e.errorCode !== 'PD_TRANSFER_NOT_CONFIRMED') return false;
    const d = (e.details ?? {}) as Record<string, unknown>;
    if (typeof d.text === 'string' && typeof d.processors_hash === 'string') {
      setNotice({
        text: d.text,
        processors_hash: d.processors_hash,
        agreement_id: typeof d.agreement_id === 'number' ? d.agreement_id : null,
        agreement_version: typeof d.agreement_version === 'number' ? d.agreement_version : 0,
        body_sha256: typeof d.body_sha256 === 'string' ? d.body_sha256 : undefined
      });
    }
    setConfirmed(false);
    return true;
  }, []);

  const payload = {
    pd_transfer_confirmed: confirmed && !!notice,
    pd_transfer_processors_hash: notice?.processors_hash ?? ''
  };

  return { notice, confirmed, setConfirmed, loading, error, payload, applyServerError };
}
